import { useState } from "react"
import { JobbinInputWhite } from "../../components/jobbinInputWhite"
import { LoadingComponent } from "../../components/loading"
import { JobbinPrimaryButton } from "../../components/primaryButton"
import { useLoading } from "../../context/loading"
import { NewUser } from "./model"
import { useNavigate } from "react-router-dom"
import { useSignupUser } from "../../hooks/auth-hooks"

const validatePassword = (password: string): string | null => {
    if (password.length < 8) return "Parola trebuie să conțină cel puțin 8 caractere"
    if (password.length > 30) return "Parola nu poate depăși 30 de caractere"
    if (!/[A-Z]/.test(password)) return "Parola trebuie să conțină cel puțin o literă mare"
    if (!/[a-z]/.test(password)) return "Parola trebuie să conțină cel puțin o literă mică"
    if (!/[0-9]/.test(password)) return "Parola trebuie să conțină cel puțin o cifră"
    if (!/[!@#$%^&*()\-_=+\[\]{}|;:,.<>?/\\]/.test(password)) {
        return "Parola trebuie să conțină cel puțin un caracter special"
    }
    return null
}

export const SignupPage = () => {
    const { loading, setLoading } = useLoading()
    const { createUser } = useSignupUser()
    const navigate = useNavigate()

    const [email, setEmail] = useState("")
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [location, setLocation] = useState("")
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [businessName, setBusinessName] = useState("")
    const [error, setError] = useState<string | null>(null)
    const [acceptedTerms, setAcceptedTerms] = useState(false)
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)

    const doSignup = async () => {
        setError(null)

        if (!email || !firstName || !lastName || !password || !confirmPassword) {
            setError("Toate câmpurile sunt obligatorii.")
            return
        }

        if (!acceptedTerms || !acceptedPrivacy) {
            setError("Trebuie să acceptați Termenii și Condițiile și să confirmați că ați citit Politica de Confidențialitate.")
            return
        }

        const passwordError = validatePassword(password)
        if (passwordError) {
            setError(passwordError)
            return
        }

        if (password !== confirmPassword) {
            setError("Parolele nu coincid.")
            return
        }

        const newUser: NewUser = {
            email,
            firstName,
            lastName,
            location,
            password,
            phoneNumber,
            businessName,
            confirmPassword,
        }

        setLoading(true)

        try {
            await createUser(newUser)
            navigate("/verify-email", { state: { email } })
        } catch (e: any) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div id="signup-page" className="bg-figmaLightBg min-h-screen">
            {loading ? <LoadingComponent /> : null}

            <div className="grid min-h-screen grid-cols-1 lg:grid-cols-12">
                <div className="col-span-1 lg:col-span-5 flex items-start lg:items-center justify-center px-4 py-8 sm:px-6 md:px-8">
                    <div className="w-full max-w-md">
                        <img
                            className="mt-2 mb-8 w-[140px] sm:w-[170px]"
                            src="/images/jobbin-logo-with-icon.png"
                            alt="Jobbin"
                        />

                        <div className="text-start">
                            <h3 className="text-figmaBlack text-[28px] sm:text-[32px] font-bold">
                                Bine ați venit!
                            </h3>
                            <p className="mt-2 text-figmaDarkGray font-bold">
                                Vă rugăm să vă înregistrați
                            </p>
                        </div>

                        {error && (
                            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <div className="mt-6 space-y-4">
                            <JobbinInputWhite
                                type="email"
                                iconStart="mail-icon.svg"
                                label="Adresa email"
                                name="email"
                                placeholder="Introduceti adresa de email"
                                onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
                            />

                            <JobbinInputWhite
                                type="text"
                                label="Nume"
                                name="first-name"
                                placeholder="Numele dumneavoastră"
                                onChange={(e) => setFirstName((e.target as HTMLInputElement).value)}
                            />

                            <JobbinInputWhite
                                type="text"
                                label="Prenume"
                                name="last-name"
                                placeholder="Prenumele dumneavoastră"
                                onChange={(e) => setLastName((e.target as HTMLInputElement).value)}
                            />

                            <JobbinInputWhite
                                type="phone"
                                iconStart="phone-receptor.svg"
                                label="Număr de telefon"
                                name="phoneNumber"
                                placeholder="Introduceti numărul de telefon"
                                onChange={(e) => setPhoneNumber((e.target as HTMLInputElement).value)}
                            />

                            <JobbinInputWhite
                                type="text"
                                label="Numele locației"
                                name="location-name"
                                placeholder="Introduceti numele locației"
                                onChange={(e) => setBusinessName((e.target as HTMLInputElement).value)}
                            />

                            <JobbinInputWhite
                                type="text"
                                label="Adresa locației"
                                name="location-address"
                                placeholder="Introduceti adresa locației"
                                onChange={(e) => setLocation((e.target as HTMLInputElement).value)}
                            />

                            <JobbinInputWhite
                                type="password"
                                iconStart="locket.svg"
                                label="Parola"
                                name="parola"
                                placeholder="Parola dumneavoastră"
                                onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
                            />

                            <JobbinInputWhite
                                type="password"
                                iconStart="locket.svg"
                                label="Confirmați parola"
                                name="confirm-password"
                                placeholder="Confirmați parola"
                                onChange={(e) => setConfirmPassword((e.target as HTMLInputElement).value)}
                            />
                        </div>

                        <p
                            className="mt-4 text-figmaBlack font-bold cursor-pointer text-sm sm:text-base"
                            onClick={() => navigate("/")}
                        >
                            Aveți deja un cont? Logați-vă aici
                        </p>

                        <div className="mt-6 space-y-3">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 accent-figmaPrimary shrink-0"
                                />
                                <span className="text-sm text-figmaBlack leading-relaxed">
                                    Am citit și accept{" "}
                                    <a
                                        href="https://jobbin.ro/termeni.html"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-figmaPrimary underline font-semibold"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Termenii și Condițiile
                                    </a>
                                </span>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={acceptedPrivacy}
                                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 accent-figmaPrimary shrink-0"
                                />
                                {/*
                                    Politica de confidentialitate este o informare conform art. 13 GDPR,
                                    nu un acord care se "accepta". Bifa confirma doar luarea la cunostinta.
                                */}
                                <span className="text-sm text-figmaBlack leading-relaxed">
                                    Am citit și am înțeles{" "}
                                    <a
                                        href="https://jobbin.ro/confidentialitate.html"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-figmaPrimary underline font-semibold"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Politica de Confidențialitate
                                    </a>
                                </span>
                            </label>
                        </div>

                        <div className="mt-6 mb-6">
                            <JobbinPrimaryButton onClickAction={doSignup} text="Înregistrare" />
                        </div>
                    </div>
                </div>

                <div className="hidden lg:flex lg:col-span-7 bg-figmaWhite items-center justify-center p-8">
                    <img
                        className="max-w-full h-auto"
                        src="/images/jobbin-logo-with-icon.png"
                        alt="Jobbin"
                    />
                </div>
            </div>
        </div>
    )
}